import { Head, Link } from "@inertiajs/react"
import { Phone } from "@untitledui/icons"
import { formatDistanceToNow } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { Badge } from "@/components/ui/badge"
import { EmptyState } from "@/components/ui/empty-state"
import { AdminLayout } from "@/layouts/admin-layout"
import { adminCallListPath } from "@/routes"

import type { ScopeSummary } from "./types"

interface Props {
  scopes: ScopeSummary[]
}

export default function AdminCallListsIndex({ scopes }: Props) {
  return (
    <AdminLayout>
      <Head title="Call lists" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "call list" }, { label: "lists" }]}
          title="Call lists"
          description="One list per Region or Sub-region. Each is ranked fresh for you every day from the last refresh."
        />

        {scopes.length === 0 ? (
          <EmptyState size="md" className="my-12">
            <EmptyState.Header>
              <EmptyState.FeaturedIcon
                icon={Phone}
                color="gray"
                theme="modern"
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <EmptyState.Title>No call lists yet</EmptyState.Title>
              <EmptyState.Description>
                A scope is a Region or Sub-region from Salesforce. Add one with
                <code className="mx-1">rake call_list:add_scope</code> and
                refresh it.
              </EmptyState.Description>
            </EmptyState.Content>
          </EmptyState>
        ) : (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {scopes.map((scope) => (
              <li key={scope.id}>
                <Link
                  href={adminCallListPath(scope.id)}
                  className="border-secondary bg-primary hover:border-brand flex h-full flex-col gap-2 rounded-xl border p-5 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-primary text-md font-semibold">
                      {scope.name}
                    </h2>
                    {!scope.active && (
                      <Badge color="gray" size="sm">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  <p className="text-tertiary text-sm">{scope.label}</p>
                  <p className="text-tertiary text-sm">
                    {scope.total} {scope.total === 1 ? "school" : "schools"} ·{" "}
                    {scope.last_refreshed_at
                      ? `refreshed ${formatDistanceToNow(new Date(scope.last_refreshed_at), { addSuffix: true })}`
                      : "never refreshed"}
                    {scope.refreshing ? " · refreshing now" : ""}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  )
}
